import React from 'react';
import {
  getDeviceGains,
  isResizable,
  canReadFactoryState,
  connectionStatusColAccessor,
  protectedColAccessor,
  factoryColAccessor,
  swVersionColAccessor,
  getDeviceAzimuthsAndDowntilts,
  canReadProtectedState,
  genModelColAccessor,
  wifiColAccessor,
  lockedUnlockedColAccessor,
  deviceTypeColAccessor,
  modelColAccessor,
  genColAccessor,
  locationColAccessor
} from './utils';
import DeviceTableColumnHeader from './DeviceTableHeaders/DeviceTableColumnHeader';
import DeviceTableAlarmCell from './DeviceTableCells/DeviceTableAlarmCell';
import { getMainUnitFromBundleMembers } from 'common/utils/helpers';
import { isItBundle } from 'common/utils/deviceTwinUtils';
import DeviceTableProtectedCell from './DeviceTableCells/DeviceTableProtectedCell';
import DeviceTableFactoryCell from './DeviceTableCells/DeviceTableFactoryCell';
import DeviceTablePodNameAndCodeHeader from './DeviceTableHeaders/DeviceTablePodNameAndCodeHeader';
import DeviceTablePodNameAndCodeCell from './DeviceTableCells/DeviceTablePodNameAndCodeCell';
import DeviceTableDeviceIdAndNameHeader from './DeviceTableHeaders/DeviceTableDeviceIdAndNameHeader';
import DeviceTableDeviceIdAndNameCell from './DeviceTableCells/DeviceTableDeviceIdAndNameCell';
import DeviceTableEchoIdAndNameHeader from './DeviceTableHeaders/DeviceTableEchoIdAndNameHeader';
import DeviceTableEchoIdAndNameCell from './DeviceTableCells/DeviceTableEchoIdAndNameCell';
import DeviceTableCell from './DeviceTableCells/DeviceTableCell';

const MIN_WIDTH = 100;

const getColumnWidth = (tableWidth, columns, isFirstRow = false) => {
  tableWidth -= 40;
  const numOfCols = columns?.length;
  let remainingWidth = tableWidth - 200;
  let widthPerCol = remainingWidth / (numOfCols - 1);

  if (isFirstRow) {
    return widthPerCol > 200 ? widthPerCol : 200;
  } else {
    return widthPerCol > 100 ? widthPerCol : 100;
  }
};

const DeviceIdAndNameCol = (parent, column, onlyDeviceId = false) => {
  // Set a default column width value
  let colWidth = 200;

  // Check if a stored value for column width exists in localStorage
  if (localStorage.getItem('deviceColumnWidth')) {
    // If a value exists, override the default colWidth with the stored value
    colWidth = localStorage.getItem('deviceColumnWidth');
  }
  return column?.selected || parent.props.forAlarmMap
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          ...(!onlyDeviceId && { fixed: 'left' }),
          Header: (col) => {
            return (
              <DeviceTableDeviceIdAndNameHeader
                forAlarmMap={parent.props.forAlarmMap}
                hasCheckbox={parent.props.hasCheckbox}
                deviceTableSet={parent.props.deviceTableSet}
                devices={parent.props.devices}
                onlyDeviceId={onlyDeviceId}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: onlyDeviceId ? MIN_WIDTH : 200,
            width: onlyDeviceId ? getColumnWidth(parent.props.deviceTableWidth, parent.columns, true) : colWidth
          }),
          ...(parent.props.forAlarmMap && { minWidth: 190 }),
          accessor: (device) => device,
          Cell: (row) => {
            return (
              <DeviceTableDeviceIdAndNameCell
                row={row}
                forAlarmMap={parent.props.forAlarmMap}
                onlyDeviceId={onlyDeviceId}
              />
            );
          }
        }
      ]
    : [];
};

const PodNameAndCodeCol = (parent, column) => {
  // Set a default column width value
  let colWidth = 200;

  // Check if a stored value for column width exists in localStorage
  if (localStorage.getItem('podColumnWidth')) {
    // If a value exists, override the default colWidth with the stored value
    colWidth = localStorage.getItem('podColumnWidth');
  }
  return column?.selected
    ? [
        {
          id: column?.id,
          fixed: 'left',
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTablePodNameAndCodeHeader
                forAlarmMap={parent.props.forAlarmMap}
                hasCheckbox={parent.props.hasCheckbox}
                deviceTableSet={parent.props.deviceTableSet}
                devices={parent.props.devices}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: 200,
            width: colWidth || getColumnWidth(parent.props.deviceTableWidth, parent.columns, true)
          }),
          accessor: (device) => device,
          Cell: (row) => {
            return <DeviceTablePodNameAndCodeCell row={row} forAlarmMap={parent.props.forAlarmMap} />;
          }
        }
      ]
    : [];
};

const PodCodeCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'podCode'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => device?.code || 'Unknown',
          Cell: (row) => <DeviceTableCell displayString={row.value} />
        }
      ]
    : [];
};

const EchoIdAndNameCol = (parent, column, onlyDeviceId = false) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          fixed: 'left',
          Header: () => {
            return (
              <DeviceTableEchoIdAndNameHeader
                forAlarmMap={parent.props.forAlarmMap}
                hasCheckbox={parent.props.hasCheckbox}
                deviceTableSet={parent.props.deviceTableSet}
                devices={parent.props.devices}
                onlyDeviceId={onlyDeviceId}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: onlyDeviceId ? 100 : 200,
            width: onlyDeviceId ? 100 : getColumnWidth(parent.props.deviceTableWidth, parent.columns, true)
          }),
          accessor: (device) => device,
          Cell: (row) => (
            <DeviceTableEchoIdAndNameCell
              row={row}
              forAlarmMap={parent.props.forAlarmMap}
              onlyDeviceId={onlyDeviceId}
            />
          )
        }
      ]
    : [];
};

const EchoNameCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: () => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'userLabel'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => device?.tags?.userLabel || 'Unknown',
          Cell: (row) => <DeviceTableCell displayString={row.value} />
        }
      ]
    : [];
};

const SWVersionCol = (parent, column) => {
  return column.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'activeSWVer'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => swVersionColAccessor(device),
          Cell: (row) => <DeviceTableCell displayString={row.value} />
        }
      ]
    : [];
};

const GenCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'genmodel'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => genColAccessor(device),
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} />;
          }
        }
      ]
    : [];
};

const ModelCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'genmodel'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => modelColAccessor(device),
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} />;
          }
        }
      ]
    : [];
};

const GenModelCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'genmodel'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => genModelColAccessor(device),
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} />;
          }
        }
      ]
    : [];
};

const AlarmsCol = (parent, column) => {
  return column?.selected || parent.props.forAlarmMap
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                customClassName={'DeviceTable_HeaderDiv_Center'}
                text={column?.uiName}
                sortName={'alarmState'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          ...(parent.props.forAlarmMap && { minWidth: 62 }),
          accessor: (device) => {
            /* Accessing alarm directly from here requires passing alarms into the parent, which means the parent will rerender when alarms update,
               and so the scroll bar would reset to the top every time alarms are polled. Instead, we will pass just the device ID to the cell, and
               the cell will handle accessing the alarm table */
            let isBundle = isItBundle(device);

            return {
              device,
              isBundle,
              deviceId: isBundle ? getMainUnitFromBundleMembers(device?.members)?.deviceId : device.deviceId
            };
          },
          Cell: (row) => {
            return (
              <DeviceTableAlarmCell
                device={row.value.device}
                productType={
                  row.original && row.original.parameters && row.original.parameters.productType
                    ? row.original.parameters.productType
                    : row.original.type
                }
                forAlarmMap={parent.props.forAlarmMap ? true : false}
              />
            );
          }
        }
      ]
    : [];
};

const LockedUnlockedCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: () => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'adminState'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => lockedUnlockedColAccessor(device),
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} />;
          }
        }
      ]
    : [];
};

const DeviceTemperatureCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: () => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                noSort={true}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => {
            return isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;
          },
          Cell: (row) => <DeviceTableCell displayString={'Unknown'} />
        }
      ]
    : [];
};

const WiFiEnabledCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: () => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'wifi'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => wifiColAccessor(device),
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} />;
          }
        }
      ]
    : [];
};

const ConnectionStatusCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'connectionState'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => {
            //if above did not return, the connectionState must be "Connected"
            //check if device is DU bundled with other SUs, if yes, return "Offline" if any SU is "Disconnected"; otherwise, return "Online"
            return connectionStatusColAccessor(device);
          },
          Cell: (row) => {
            const { connectionStatus, lastActivityTime } = row.value;
            const isOnline = connectionStatus === 'Online';
            const offlineDataTip = `Offline since ${lastActivityTime}`;
            const textClassName = isOnline ? 'DeviceTable_OnlineText' : 'DeviceTable_OfflineText';
            const dataTip = isOnline ? connectionStatus : offlineDataTip;

            return (
              <div className="DeviceTable_cellWrapper" data-tip={dataTip}>
                <div className={'DeviceTable_ConnectionStatus ' + textClassName}>{connectionStatus}</div>
              </div>
            );
          }
        }
      ]
    : [];
};

const ProtectedCol = (parent, column) => {
  return column?.selected && canReadProtectedState(parent.props.userPermissions)
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                customClassName={'DeviceTable_HeaderDiv_Center'}
                text={column?.uiName}
                noSort={true}
                // sortName={null}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => {
            return protectedColAccessor(device, parent.props.protectedList, parent.props.userPermissions);
          },
          Cell: (row) => {
            return <DeviceTableProtectedCell data={row.value} />;
          }
        }
      ]
    : [];
};

const FactoryCol = (parent, column) => {
  return column?.selected && canReadFactoryState(parent.props.userPermissions)
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                customClassName={'DeviceTable_HeaderDiv_Center'}
                text={column?.uiName}
                noSort={true}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => {
            return factoryColAccessor(device, parent.props.factoryList, parent.props.userPermissions);
          },
          Cell: (row) => {
            return <DeviceTableFactoryCell data={row.value} />;
          }
        }
      ]
    : [];
};

const DeviceTypeCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                sortName={'productType'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => deviceTypeColAccessor(device),
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} />;
          }
        }
      ]
    : [];
};

const GainsCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            let sortName = null;
            switch (column.id) {
              case 'dl_gain_v_col':
                sortName = 'vdl_gain';
                break;
              case 'dl_gain_h_col':
                sortName = 'hdl_gain';
                break;
              case 'ul_gain_v_col':
                sortName = 'vul_gain';
                break;
              case 'ul_gain_h_col':
                sortName = 'hul_gain';
                break;
              default:
                break;
            }
            return (
              <DeviceTableColumnHeader
                customClassName={'DeviceTable_HeaderDiv_Center'}
                text={column?.uiName}
                sortName={sortName}
                // noSort={true}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => {
            const gains = getDeviceGains(device);
            let displayString = 'Unknown';

            switch (column.id) {
              case 'dl_gain_v_col':
                displayString = gains.dlv;
                break;
              case 'dl_gain_h_col':
                displayString = gains.dlh;
                break;
              case 'ul_gain_v_col':
                displayString = gains.ulv;
                break;
              case 'ul_gain_h_col':
                displayString = gains.ulh;
                break;
              default:
                break;
            }
            return displayString;
          },
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} center={true} />;
          }
        }
      ]
    : [];
};

const AzimuthCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                customClassName={'DeviceTable_HeaderDiv_Center'}
                text={column?.uiName}
                sortName={'beamAzimuth'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => {
            const data = getDeviceAzimuthsAndDowntilts(device);
            let displayString = 'Unknown';

            switch (column.id) {
              case 'azimuth_absolute_col':
                displayString = data.absoluteAz;
                break;
              case 'azimuth_electrical_col':
                displayString = data.electricalAz;
                break;
              case 'azimuth_mechanical_col':
                displayString = data.mechanicalAz;
                break;
              default:
                break;
            }
            return displayString;
          },
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} center={true} />;
          }
        }
      ]
    : [];
};

const DowntiltCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                customClassName={'DeviceTable_HeaderDiv_Center'}
                text={column?.uiName}
                sortName={'beamElevation'}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: MIN_WIDTH,
            width: getColumnWidth(parent.props.deviceTableWidth, parent.columns)
          }),
          accessor: (device) => {
            const data = getDeviceAzimuthsAndDowntilts(device);
            let displayString = 'Unknown';

            switch (column.id) {
              case 'downtilt_absolute_col':
                displayString = data.absoluteDowntilt;
                break;
              case 'downtilt_electrical_col':
                displayString = data.electricalDowntilt;
                break;
              case 'downtilt_mechanical_col':
                displayString = data.mechanicalDowntilt;
                break;
              default:
                break;
            }
            return displayString;
          },
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} center={true} />;
          }
        }
      ]
    : [];
};

const LocationCol = (parent, column) => {
  return column?.selected
    ? [
        {
          id: column?.id,
          resizable: isResizable(parent.columns, column?.id),
          Header: (col) => {
            return (
              <DeviceTableColumnHeader
                text={column?.uiName}
                // sorting is removed for black widow under the ticket IBMS-5130
                // sortName={'location'}
                noSort={true}
                forAlarmMap={parent.props.forAlarmMap}
                hasResizeSlider={isResizable(parent.columns, column?.id)}
              />
            );
          },
          ...(!parent.props.forAlarmMap && {
            minWidth: 200,
          }),
          accessor: (device) => locationColAccessor(device),
          Cell: (row) => {
            return <DeviceTableCell displayString={row.value} />;
          }
        }
      ]
    : [];
};

export const getAlarmMapColumns = (parent, columns) => {
  return [...DeviceIdAndNameCol(parent, columns?.device_id_col), ...AlarmsCol(parent, columns?.alarms_col)];
};

export const getPivotTabColumns = (parent, columns) => [
  ...PodNameAndCodeCol(parent, columns?.pod_name_col),
  ...PodCodeCol(parent, columns?.pod_code_col),
  ...GenCol(parent, columns?.gen_col),
  ...LocationCol(parent, columns?.location_col),
  ...AlarmsCol(parent, columns?.alarms_col),
  ...ConnectionStatusCol(parent, columns?.connection_status_col),
  ...SWVersionCol(parent, columns?.sw_version_col),
  ...DeviceIdAndNameCol(parent, columns?.device_id_col, true),
  ...GainsCol(parent, columns?.dl_gain_h_col),
  ...GainsCol(parent, columns?.dl_gain_v_col),
  ...GainsCol(parent, columns?.ul_gain_h_col),
  ...GainsCol(parent, columns?.ul_gain_v_col),
  ...LockedUnlockedCol(parent, columns?.locked_unlocked_col),
  ...WiFiEnabledCol(parent, columns?.wifi_enabled_col),
  // ...AzimuthCol(parent, columns?.azimuth_absolute_col),
  ...AzimuthCol(parent, columns?.azimuth_electrical_col),
  // ...AzimuthCol(parent, columns?.azimuth_mechanical_col),
  // ...DowntiltCol(parent, columns?.downtilt_absolute_col),
  ...DowntiltCol(parent, columns?.downtilt_electrical_col)
  // ...DowntiltCol(parent, columns?.downtilt_mechanical_col),
  // ...ProtectedCol(parent, columns?.protected_col),
  // ...FactoryCol(parent, columns?.factory_col)
];

export const getEchoTabColumns = (parent, columns) => [
  ...EchoIdAndNameCol(parent, columns?.echo_id_col),
  ...EchoNameCol(parent, columns?.echo_name_col),
  ...ModelCol(parent, columns?.model_col),
  ...LocationCol(parent, columns?.location_col),
  ...AlarmsCol(parent, columns?.alarms_col),
  ...ConnectionStatusCol(parent, columns?.connection_status_col),
  ...SWVersionCol(parent, columns?.sw_version_col),
  ...DeviceIdAndNameCol(parent, columns?.device_id_col, true),
  ...GainsCol(parent, columns?.dl_gain_h_col),
  ...GainsCol(parent, columns?.dl_gain_v_col),
  ...GainsCol(parent, columns?.ul_gain_h_col),
  ...GainsCol(parent, columns?.ul_gain_v_col),
  ...LockedUnlockedCol(parent, columns?.locked_unlocked_col),
  ...WiFiEnabledCol(parent, columns?.wifi_enabled_col),
  // ...DeviceTemperatureCol(parent, columns?.device_temperature_col),
  // ...AzimuthCol(parent, columns?.azimuth_absolute_col),
  ...AzimuthCol(parent, columns?.azimuth_electrical_col),
  // ...AzimuthCol(parent, columns?.azimuth_mechanical_col),
  // ...DowntiltCol(parent, columns?.downtilt_absolute_col),
  // ...DowntiltCol(parent, columns?.downtilt_mechanical_col),
  ...DowntiltCol(parent, columns?.downtilt_electrical_col)
  // ...ProtectedCol(parent, columns?.protected_col),
  // ...FactoryCol(parent, columns?.factory_col)
];

export const getAllDevicesTabColumns = (parent, columns) => [
  ...DeviceIdAndNameCol(parent, columns?.device_id_col),
  ...GenModelCol(parent, columns?.gen_model_col),
  ...LocationCol(parent, columns?.location_col),
  ...DeviceTypeCol(parent, columns?.device_type_col),
  ...SWVersionCol(parent, columns?.sw_version_col),
  ...AlarmsCol(parent, columns?.alarms_col),
  ...ConnectionStatusCol(parent, columns?.connection_status_col)
  // ...ProtectedCol(parent, columns?.protected_col),
  // ...FactoryCol(parent, columns?.factory_col)
];
